// Statistik for et enkelt spil, samme facon som klub-statistikken fra backend
// (api.stats.ts), men beregnet i browseren ud fra spillets runder.
import { distribute, inputFromRound, sittingOut } from './scoring'
import type { Player } from './scoring'
import type { BidType, TrumpSuit } from '../../../shared/types'

const SOLO_TYPES = ['Sol', 'Ren sol', 'Ren bordlægger', 'Dobbelt bordlægger']

export interface GameStatsPlayerInput {
  userId: number
  name: string
  position: number
  totalScore: number
}

export interface GameStatsRoundInput {
  roundNumber: number
  bidderId: number
  partnerId?: number
  bidType: BidType
  trumpSuit?: TrumpSuit
  tricksTaken: number
  won?: boolean
  score: number
}

export interface GameStatsInput {
  players: GameStatsPlayerInput[]
  rounds: GameStatsRoundInput[]
}

export interface GameStatsBid {
  bidType: BidType
  count: number
  won: number
  winRate: number
}

export interface GameStatsPartner {
  a: number
  b: number
  aName: string
  bName: string
  rounds: number
  won: number
  points: number
}

export interface GameStatsBidderPartner {
  bidderId: number
  partnerId: number
  count: number
  won: number
}

export interface GameStatsTrump {
  suit: TrumpSuit
  count: number
  won: number
  winRate: number
}

export interface GameStatsPlayerCounter {
  count: number
  won: number
}

export interface GameStatsPlayer {
  userId: number
  name: string
  asBidder: GameStatsPlayerCounter
  asPartner: GameStatsPlayerCounter
  solos: GameStatsPlayerCounter
  favoriteBid: BidType | null
  favoriteTrump: TrumpSuit | null
  points: number
}

export interface GameStatsBiggestRound {
  roundNumber: number
  bidderName: string
  bidType: BidType
  score: number
}

export interface GameStatsRecords {
  biggestRound: GameStatsBiggestRound | null
}

export interface GameStats {
  bids: GameStatsBid[]
  partners: GameStatsPartner[]
  bidderPartners: GameStatsBidderPartner[]
  trumps: GameStatsTrump[]
  players: GameStatsPlayer[]
  records: GameStatsRecords
}

function winRateOf(won: number, count: number): number {
  return count === 0 ? 0 : won / count
}

function favoriteOf<T extends string>(counts: Map<T, number>): T | null {
  let best: T | null = null
  let bestCount = 0
  for (const [key, count] of counts) {
    if (count > bestCount) {
      best = key
      bestCount = count
    }
  }
  return best
}

/** Beregner meldinger, makkere, trumf, spillere og rekorder for ét spil. */
export function computeGameStats(game: GameStatsInput): GameStats {
  const players: Player[] = game.players.map((p) => ({ userId: p.userId, name: p.name }))
  const nameOf = (userId: number): string => game.players.find((p) => p.userId === userId)?.name ?? ''

  const bidCounters = new Map<BidType, { count: number; won: number }>()
  const trumpCounters = new Map<TrumpSuit, { count: number; won: number }>()
  const partnerCounters = new Map<string, GameStatsPartner>()
  const bidderPartnerCounters = new Map<string, GameStatsBidderPartner>()

  const playerState = new Map<
    number,
    {
      asBidder: GameStatsPlayerCounter
      asPartner: GameStatsPlayerCounter
      solos: GameStatsPlayerCounter
      bidCounts: Map<BidType, number>
      trumpCounts: Map<TrumpSuit, number>
      points: number
    }
  >()
  for (const p of game.players) {
    playerState.set(p.userId, {
      asBidder: { count: 0, won: 0 },
      asPartner: { count: 0, won: 0 },
      solos: { count: 0, won: 0 },
      bidCounts: new Map(),
      trumpCounts: new Map(),
      points: 0
    })
  }

  let biggestRound: GameStatsBiggestRound | null = null

  game.rounds.forEach((round) => {
    const roundIndex = round.roundNumber - 1
    const isSolo = (SOLO_TYPES as string[]).includes(round.bidType)
    const won = round.score > 0
    const out = sittingOut(players, roundIndex)
    const dist = distribute(players, roundIndex, inputFromRound(round))

    for (const p of game.players) {
      if (out.includes(p.userId)) continue
      const state = playerState.get(p.userId)
      if (state) state.points += dist[p.userId] ?? 0
    }

    const bidEntry = bidCounters.get(round.bidType) ?? { count: 0, won: 0 }
    bidEntry.count += 1
    if (won) bidEntry.won += 1
    bidCounters.set(round.bidType, bidEntry)

    if (!isSolo && round.trumpSuit) {
      const trumpEntry = trumpCounters.get(round.trumpSuit) ?? { count: 0, won: 0 }
      trumpEntry.count += 1
      if (won) trumpEntry.won += 1
      trumpCounters.set(round.trumpSuit, trumpEntry)
    }

    const bidderState = playerState.get(round.bidderId)
    if (bidderState) {
      if (isSolo) {
        bidderState.solos.count += 1
        if (won) bidderState.solos.won += 1
      } else {
        bidderState.asBidder.count += 1
        if (won) bidderState.asBidder.won += 1
      }
      bidderState.bidCounts.set(round.bidType, (bidderState.bidCounts.get(round.bidType) ?? 0) + 1)
      if (!isSolo && round.trumpSuit) {
        bidderState.trumpCounts.set(round.trumpSuit, (bidderState.trumpCounts.get(round.trumpSuit) ?? 0) + 1)
      }
    }

    const isSelfPartner = !isSolo && round.partnerId === round.bidderId
    if (!isSolo && round.partnerId && !isSelfPartner) {
      const partnerState = playerState.get(round.partnerId)
      if (partnerState) {
        partnerState.asPartner.count += 1
        if (won) partnerState.asPartner.won += 1
        partnerState.bidCounts.set(round.bidType, (partnerState.bidCounts.get(round.bidType) ?? 0) + 1)
        if (round.trumpSuit) {
          partnerState.trumpCounts.set(round.trumpSuit, (partnerState.trumpCounts.get(round.trumpSuit) ?? 0) + 1)
        }
      }

      const a = Math.min(round.bidderId, round.partnerId)
      const b = Math.max(round.bidderId, round.partnerId)
      const key = `${a}-${b}`
      const entry =
        partnerCounters.get(key) ?? { a, b, aName: nameOf(a), bName: nameOf(b), rounds: 0, won: 0, points: 0 }
      entry.rounds += 1
      if ((dist[round.bidderId] ?? 0) > 0) entry.won += 1
      entry.points += dist[round.bidderId] ?? 0
      partnerCounters.set(key, entry)
    }

    if (!isSolo && round.partnerId) {
      const key = `${round.bidderId}-${round.partnerId}`
      const entry =
        bidderPartnerCounters.get(key) ?? { bidderId: round.bidderId, partnerId: round.partnerId, count: 0, won: 0 }
      entry.count += 1
      if (won) entry.won += 1
      bidderPartnerCounters.set(key, entry)
    }

    const bidderScore = round.score
    if (!biggestRound || Math.abs(bidderScore) > Math.abs(biggestRound.score)) {
      biggestRound = {
        roundNumber: round.roundNumber,
        bidderName: nameOf(round.bidderId),
        bidType: round.bidType,
        score: bidderScore
      }
    }
  })

  const bids: GameStatsBid[] = Array.from(bidCounters.entries()).map(([bidType, c]) => ({
    bidType,
    count: c.count,
    won: c.won,
    winRate: winRateOf(c.won, c.count)
  }))

  const trumps: GameStatsTrump[] = Array.from(trumpCounters.entries()).map(([suit, c]) => ({
    suit,
    count: c.count,
    won: c.won,
    winRate: winRateOf(c.won, c.count)
  }))

  const partners: GameStatsPartner[] = Array.from(partnerCounters.values())
  const bidderPartners: GameStatsBidderPartner[] = Array.from(bidderPartnerCounters.values())

  const statsPlayers: GameStatsPlayer[] = game.players.map((p) => {
    const state = playerState.get(p.userId)
    return {
      userId: p.userId,
      name: p.name,
      asBidder: state?.asBidder ?? { count: 0, won: 0 },
      asPartner: state?.asPartner ?? { count: 0, won: 0 },
      solos: state?.solos ?? { count: 0, won: 0 },
      favoriteBid: state ? favoriteOf(state.bidCounts) : null,
      favoriteTrump: state ? favoriteOf(state.trumpCounts) : null,
      points: state?.points ?? 0
    }
  })

  return {
    bids,
    partners,
    bidderPartners,
    trumps,
    players: statsPlayers,
    records: { biggestRound }
  }
}
